import { FunctionComponent, useEffect, useRef, useState } from "react";
import "./PolicyTemplate.scss";
import Table from "../../Components/Table/Table";
import CustomToolbar from "../../Components/Table/components/CustomToolbar";
import ButtonField from "../../Components/Button/ButtonField";
import { Columns } from "../../Components/Table/ITable";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import Dialog from "../../Components/Dialog/Dialog";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";
import TableSearch from "../../Components/Table/components/TableSearch";
import { ReactComponent as Delete } from "../../Assets/Images/delete.svg";
import { ReactComponent as Edit } from "../../Assets/Images/edit.svg";
import { IDeleteDialog } from "../../Components/DeleteDialog/IDeleteDialog";
import IconButtonField from "../../Components/Button/IconButtonField";
import { SvgIcon } from "@mui/material";
import DeleteDialog from "../../Components/DeleteDialog/DeleteDialog";
import { ReactComponent as RefreshButton } from "../../Assets/Images/RefreshButton.svg";

// Form + Types
import PolicyTemplateForm from "./PolicyTemplateForm";
import { IPolicyTemplate } from "./IPolicyTemplate";
import { clearPolicyMasterRead, PolicyMasterRead } from "../../Redux/Slices/PrivacyPolicy/PolicyMasterRead";
import { clearPolicyCreate_Update, PolicyCreate_Update } from "../../Redux/Slices/PrivacyPolicy/policyMasterCreate_Update";



interface PolicyTemplateProps { }

const PolicyTemplate: FunctionComponent<PolicyTemplateProps> = () => {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleteDialogData, setDeleteData] = useState<IDeleteDialog>({});
  const [initialValues, setInitialValues] = useState<IPolicyTemplate | {}>({
    status: true,
  });

  const dispatch = useAppDispatch();

  const { data: listPolicies } = useAppSelector(
    (state) => state.ListPolicyMaster
  );
  const { data: addOrUpdatePolicy } = useAppSelector(
    (state) => state.Add_update_policyMaster
  );

  const formRef = useRef<HTMLFormElement>(null);

  // Table columns
  const columns: Columns = [
    {
      field: "index",
      renderHeader: () => <b>S.No</b>,
      width: 90,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "template_name",
      renderHeader: () => <b>Template Name</b>,
      flex: 1,
      type: "string",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "status",
      renderHeader: () => <b>Status</b>,
      flex: 1,
      type: "string",
      headerAlign: "center",
      align: "center",
      cellClassName: (params) =>
        `activename ${params.row.status ? "active" : "inactive"}`,
      valueFormatter: (params) =>
        params.value === true ? "Active" : "Inactive",
    },
    {
      field: "action",
      renderHeader: () => <b>Action</b>,
      flex: 1,
      type: "actions",
      renderCell: (params: GridRenderCellParams<IPolicyTemplate>) => {
        return (
          <>
            <IconButtonField
              onClick={() => {
                setInitialValues({ ...params.row, template_id: params.row.template_id });
                setIsEdit(true);
                setDialogOpen(true);
              }}
            >
              <SvgIcon sx={{ fill: "none" }} component={Edit} inheritViewBox />
            </IconButtonField>
            <IconButtonField
              onClick={() => {
                dispatch(clearPolicyCreate_Update());
                setDeleteData(params.row);
                setDeleteDialogOpen(true);
              }}
            >
              <SvgIcon component={Delete} inheritViewBox />
            </IconButtonField>
          </>
        );
      },
    },
  ];

  // 🔑 add id for DataGrid
  const Policies = Array.isArray(listPolicies?.data)
    ? listPolicies?.data.map((policy, index) => ({
      ...policy,
      id: policy.template_id, // ensure unique key for DataGrid
      index: index + 1,
    }))
    : [];

  useEffect(() => {
    dispatch(PolicyMasterRead());
  }, [addOrUpdatePolicy, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearPolicyCreate_Update());
    };
  }, [dispatch]);

  return (
    <>
      <Table rows={Policies ?? []} columns={columns} exportFileName="Policy_Templates">
        <CustomToolbar>
          <h6>Policy Template</h6>
          <TableSearch />
          <ButtonField
            className="tabel_add_new_button"
            onClick={() => {
              setInitialValues({ status: true });
              setIsEdit(false);
              setDialogOpen(true);
            }}
            toolTip="Add new"
          >
            Add new
          </ButtonField>
          <IconButtonField
            className="refresh_btn"
            toolTip="Refresh"
            onClick={() => dispatch(PolicyMasterRead())}
          >
            <SvgIcon
              className="refresh_svg_icon"
              component={RefreshButton}
              inheritViewBox
            />
          </IconButtonField>
        </CustomToolbar>
      </Table>

      <Dialog
        open={dialogOpen}
        dialogClassName="dialog_double_input"
        Dialog_Title={`${isEdit ? "Update" : "Add"} Policy`}
        Dialog_Content={
          <PolicyTemplateForm
            formRef={formRef}
            initialValues={initialValues}
            setInitialValues={setInitialValues}
          />
        }
        Dialog_Actions={
          <>
            <ButtonField
              className="form_cancel_button"
              onClick={() => {
                setDialogOpen(false);
                setIsEdit(false);
              }}
            >
              Cancel
            </ButtonField>
            <ButtonField
              className="form_save_button"
              onClick={() => formRef.current?.requestSubmit()}
            >
              Save
            </ButtonField>
          </>
        }
      />

      <ResponseDialog
        okFunction={() => setDialogOpen(false)}
        response={addOrUpdatePolicy}
        parentDialog={setDialogOpen}
      />

      {(deleteDialogData && deleteDialogOpen) && (
        <DeleteDialog
          deleteableName={deleteDialogData.name ?? ""}
          open={deleteDialogOpen}
          setOpen={setDeleteDialogOpen}
          deleteFunction={() => {
            if (deleteDialogData.id) {
              dispatch(
                PolicyCreate_Update({
                  ...deleteDialogData,
                  is_delete: true,
                })
              );
            }
          }}
          response={addOrUpdatePolicy}
        />
      )}
    </>
  );
};

export default PolicyTemplate;
